import ArticleUpdaterService from "@components/Article/ArticleUpdater/ArticleUpdaterService";
import { useRouter } from "@core/Api/useRouter";
import FetchService from "@core-ui/ApiServices/FetchService";
import MimeTypes from "@core-ui/ApiServices/Types/MimeTypes";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import GitIndexService from "@core-ui/ContextServices/GitIndexService";
import { useArticlePropsStore } from "@core-ui/stores/ArticlePropsStore/ArticlePropsStore.provider";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import BranchUpdaterService from "@ext/git/actions/Branch/BranchUpdaterService/logic/BranchUpdaterService";
import OnBranchUpdateCaller from "@ext/git/actions/Branch/BranchUpdaterService/model/OnBranchUpdateCaller";
import { FileStatus } from "@ext/Watchers/model/FileStatus";
import { useCallback } from "react";

export type UseDiscard = {
	discard: (path: string[], reset: boolean) => Promise<void>;
};

export const useDiscard = (onDiscard?: () => void): UseDiscard => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const router = useRouter();
	const index = GitIndexService.getStatus();
	const catalogName = useCatalogPropsStore((state) => state.data?.name);
	const articlePath = useArticlePropsStore((state) => state.data?.ref?.path);

	// Only a new article disappears: a restored deleted one is back and stays open.
	const getVanishingArticlePath = useCallback(
		(paths: string[]) => {
			if (!catalogName || !articlePath?.startsWith(`${catalogName}/`)) return null;
			const repoPath = articlePath.slice(catalogName.length + 1);
			if (!paths.includes(repoPath)) return null;
			return index.get(articlePath) === FileStatus.new ? articlePath : null;
		},
		[catalogName, articlePath, index],
	);

	// A deleted article is read from the old revision (`<catalog>:<scope>/…`); once restored it has a file again.
	const isRestoringShownArticle = useCallback(
		(paths: string[]) => {
			const [catalogPart, ...rest] = articlePath?.split("/") ?? [];
			return !!catalogPart?.includes(":") && paths.includes(rest.join("/"));
		},
		[articlePath],
	);

	const discard = useCallback(
		async (paths: string[], reset: boolean) => {
			const vanishingArticlePath = getVanishingArticlePath(paths);
			const restoringShownArticle = isRestoringShownArticle(paths);
			const endpoint = apiUrlCreator.getVersionControlDiscardUrl(vanishingArticlePath);
			const res = await FetchService.fetch(endpoint, JSON.stringify(paths), MimeTypes.json);
			if (!res.ok) return;
			const redirectPath = await res.text();
			if (vanishingArticlePath && redirectPath) void router.pushPath(redirectPath);
			else if (restoringShownArticle)
				void router.pushPath(router.path, { diff: "1", oldScope: "HEAD", scope: undefined });
			else {
				await ArticleUpdaterService.update(apiUrlCreator);
				refreshPage();
			}
			onDiscard?.();

			void BranchUpdaterService.updateBranch(
				apiUrlCreator,
				reset ? OnBranchUpdateCaller.MergeRequest : OnBranchUpdateCaller.DiscardNoReset,
			);
		},
		[onDiscard, getVanishingArticlePath, isRestoringShownArticle, router],
	);

	return {
		discard,
	};
};
